import React, { useState, useEffect, useMemo } from "react";
import { Users, CreditCard, Calendar, TrendingUp, Star, MessageCircle, Download, X, Search, Bell, Send, Mail } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import axios from "axios";
import NotificationOptIn from "../components/NotificationOptIn";
import EmailComposer from "../components/EmailComposer";
import AdminWhatsApp from "../components/AdminWhatsApp";
import AdminOTPGate from "../components/AdminOTPGate";
import WeeklyDigest from "../components/WeeklyDigest";
import HoroscopeSender from "../components/HoroscopeSender";
import { AndroidAdmin } from "../components/AndroidAdmin";
import TestimonialsAdmin from "../components/TestimonialsAdmin";
import { fmtPaid, fmtInr } from "../contexts/CurrencyContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function LiveChatPush({ chat, index }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("✦ Your astrologer replied");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    if (!body.trim()) { setResult({ error: "Write the answer you want to push." }); return; }
    setSending(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/push/send`, {
        user_id: chat.user_id,
        title: title.trim() || "AstroFate",
        body: body.trim(),
        route: `/livechat/${chat.chat_id}`,
      }, { withCredentials: true });
      setResult({ ok: true, ...data });
      if (data.success_count) setBody("");
    } catch (err) {
      setResult({ error: err.response?.data?.detail || "Push failed." });
    } finally {
      setSending(false);
    }
  };

  const noDevice = !chat.user_id || !chat.push_devices;

  return (
    <>
      <button data-testid={`admin-livechat-push-toggle-${index}`} onClick={() => setOpen(o => !o)} className="btn-ghost"
        style={{ padding: "6px 14px", fontSize: "0.76rem", display: "inline-flex", alignItems: "center", gap: "6px", color: noDevice ? "rgba(255,255,255,0.4)" : "#c4b5fd", borderColor: "rgba(157,78,221,0.35)" }}
        title={noDevice ? "This customer has not installed the app yet" : "Push your answer to their phone"}>
        <Bell size={13} /> {open ? "Hide push" : "Notify by Push"}
        {chat.push_devices > 0 && (
          <span style={{ background: "rgba(34,197,94,0.16)", color: "#86efac", borderRadius: "999px", padding: "1px 7px", fontSize: "0.66rem", fontWeight: 700 }}>
            {chat.push_devices}
          </span>
        )}
      </button>
      {open && (
        <div data-testid={`admin-livechat-push-panel-${index}`} style={{ width: "100%", marginTop: "10px", background: "rgba(157,78,221,0.07)", border: "1px solid rgba(157,78,221,0.25)", borderRadius: "10px", padding: "12px" }}>
          {noDevice ? (
            <div data-testid={`admin-livechat-push-nodevice-${index}`} style={{ color: "#fca5a5", fontSize: "0.78rem" }}>
              This customer has no app device registered yet — reply in chat or email instead.
            </div>
          ) : (
            <>
              <input data-testid={`admin-livechat-push-title-${index}`} value={title} onChange={e => setTitle(e.target.value)}
                maxLength={80} placeholder="Notification title" className="cosmic-input" style={{ marginBottom: "8px" }} />
              <textarea data-testid={`admin-livechat-push-body-${index}`} value={body} onChange={e => setBody(e.target.value)}
                maxLength={240} rows={3} placeholder="Type the answer you want to deliver to their phone…"
                className="cosmic-input" style={{ resize: "vertical", minHeight: "70px" }} />
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "8px", flexWrap: "wrap" }}>
                <button data-testid={`admin-livechat-push-send-${index}`} onClick={send} disabled={sending} className="btn-purple"
                  style={{ padding: "6px 14px", fontSize: "0.76rem", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <Send size={12} /> {sending ? "Sending…" : "Send Push"}
                </button>
                {chat.last_message && (
                  <button type="button" onClick={() => setBody(chat.last_message)} className="btn-ghost" style={{ padding: "5px 11px", fontSize: "0.72rem" }}>
                    Use last message
                  </button>
                )}
                <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.7rem" }}>{body.length}/240 · opens the chat in the app</span>
              </div>
              {result?.error && (
                <div data-testid={`admin-livechat-push-error-${index}`} style={{ color: "#fca5a5", fontSize: "0.76rem", marginTop: "8px" }}>{result.error}</div>
              )}
              {result?.ok && (
                <div data-testid={`admin-livechat-push-success-${index}`} style={{ color: "#86efac", fontSize: "0.76rem", marginTop: "8px" }}>
                  Delivered to {result.success_count || 0} device{result.success_count === 1 ? "" : "s"}.
                  {result.failure_count > 0 && ` · ${result.failure_count} failed.`}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

function PushComposer({ customers }) {
  const [target, setTarget] = useState("all");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [route, setRoute] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Customer context (only when target is a specific user).
  const [context, setContext] = useState(null);
  const [ctxLoading, setCtxLoading] = useState(false);

  // AI response generator
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAnswer, setAiAnswer] = useState("");

  // Load customer context when a specific target is chosen.
  useEffect(() => {
    if (target === "all") { setContext(null); setAiAnswer(""); setAiPrompt(""); return; }
    setCtxLoading(true);
    axios.get(`${API}/admin/push/customer-context/${target}`, { withCredentials: true })
      .then(r => setContext(r.data))
      .catch(() => setContext({ error: "Failed to load customer context." }))
      .finally(() => setCtxLoading(false));
  }, [target]);

  const handleGenerateAI = async () => {
    if (!aiPrompt.trim() || target === "all") return;
    setAiLoading(true); setAiAnswer("");
    try {
      const { data } = await axios.post(
        `${API}/admin/push/generate-ai`,
        { user_id: target, prompt: aiPrompt.trim() },
        { withCredentials: true }
      );
      setAiAnswer(data.answer || "");
      // Prefill push body with a truncated version (push size limit ~240 chars).
      if (data.answer && !body) setBody((data.answer || "").slice(0, 240));
      if (!title) setTitle(`✦ From your astrologer${context?.user?.name ? ` — ${context.user.name.split(" ")[0]}` : ""}`);
    } catch (err) {
      setAiAnswer("");
      alert(err.response?.data?.detail || "AI generation failed.");
    } finally {
      setAiLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) { setResult({ error: "Title and message are required." }); return; }
    setSending(true); setResult(null);
    try {
      const payload = { title: title.trim(), body: body.trim(), route: route.trim() || null };
      if (target !== "all") payload.user_id = target;
      const { data } = await axios.post(`${API}/admin/push/send`, payload, { withCredentials: true });
      setResult({ ok: true, ...data });
      if (data.success_count) { setTitle(""); setBody(""); setRoute(""); setAiPrompt(""); setAiAnswer(""); }
    } catch (err) {
      setResult({ error: err.response?.data?.detail || "Failed to send push. Check that at least one customer has an FCM token." });
    } finally {
      setSending(false);
    }
  };

  const birth = context?.birth_details || {};
  const monthName = birth.month ? new Date(2000, birth.month - 1).toLocaleString("en-US", { month: "short" }) : "";

  return (
    <div>
      <div style={{ marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Bell size={20} style={{ color: "#FFD166" }} />
          <h2 className="font-cinzel" style={{ fontSize: "1.15rem", color: "#FFD166" }}>Send Push Notification</h2>
        </div>
        <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginTop: "4px" }}>
          Deliver instant alerts to customers who installed the AstroFate app on their phone.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: target === "all" ? "1fr" : "minmax(0, 1.35fr) minmax(280px, 1fr)", gap: "18px", alignItems: "start" }}>
        <form onSubmit={handleSend} className="glass-card" style={{ padding: "24px" }}>
          <div style={{ marginBottom: "16px" }}>
            <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", display: "block", marginBottom: "6px" }}>Recipient</label>
            <select data-testid="push-target-select" value={target} onChange={e => setTarget(e.target.value)} className="cosmic-input">
              <option value="all">All customers with the app</option>
              {(customers || []).slice(0, 500).map(c => (
                <option key={c.id} value={c.id}>{c.name || c.email} · {c.email}</option>
              ))}
            </select>
          </div>

          {/* AI helper — only when a specific customer is chosen */}
          {target !== "all" && (
            <div style={{ background: "rgba(157,78,221,0.08)", border: "1px solid rgba(157,78,221,0.25)", borderRadius: "10px", padding: "14px", marginBottom: "18px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#c4b5fd", fontSize: "0.8rem", fontWeight: 600, marginBottom: "8px" }}>
                <MessageCircle size={14} /> Generate personalised AI response
              </div>
              <textarea data-testid="push-ai-prompt" value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
                placeholder="e.g. What career opportunities does this native have in 2026?"
                rows={2} className="cosmic-input" style={{ resize: "vertical", minHeight: "48px" }} />
              <button type="button" data-testid="push-ai-generate-btn" onClick={handleGenerateAI} disabled={aiLoading || !aiPrompt.trim()}
                className="btn-purple" style={{ marginTop: "8px", padding: "7px 14px", fontSize: "0.78rem" }}>
                {aiLoading ? "Consulting AI…" : "✦ Generate with Kundli context"}
              </button>
              {aiAnswer && (
                <div data-testid="push-ai-answer" style={{ marginTop: "12px", background: "rgba(0,0,0,0.35)", borderRadius: "8px", padding: "10px 12px", color: "rgba(255,255,255,0.85)", fontSize: "0.82rem", lineHeight: 1.5, maxHeight: "180px", overflowY: "auto", whiteSpace: "pre-wrap" }}>
                  {aiAnswer}
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    <button type="button" onClick={() => setBody(aiAnswer.slice(0, 240))} className="btn-ghost" style={{ padding: "4px 10px", fontSize: "0.72rem" }}>
                      Use as message
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom: "16px" }}>
            <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", display: "block", marginBottom: "6px" }}>Title</label>
            <input data-testid="push-title-input" type="text" value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Your daily horoscope is ready" maxLength={80} className="cosmic-input" />
            <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.72rem", marginTop: "4px" }}>{title.length}/80</div>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", display: "block", marginBottom: "6px" }}>Message</label>
            <textarea data-testid="push-body-input" value={body} onChange={e => setBody(e.target.value)}
              placeholder="Message that appears in the notification tray…" maxLength={240} rows={3}
              className="cosmic-input" style={{ resize: "vertical", minHeight: "80px" }} />
            <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.72rem", marginTop: "4px" }}>{body.length}/240</div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem", display: "block", marginBottom: "6px" }}>Deep-link route (optional)</label>
            <input data-testid="push-route-input" type="text" value={route} onChange={e => setRoute(e.target.value)}
              placeholder="/chat or /consultation" className="cosmic-input" />
            <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.72rem", marginTop: "4px" }}>Opens this path inside the app when the user taps the notification.</div>
          </div>

          {result?.error && (
            <div data-testid="push-error" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: "8px", padding: "10px 14px", marginBottom: "14px", color: "#fca5a5", fontSize: "0.85rem" }}>
              {result.error}
            </div>
          )}
          {result?.ok && (
            <div data-testid="push-success" style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: "8px", padding: "10px 14px", marginBottom: "14px", color: "#86efac", fontSize: "0.85rem" }}>
              Delivered to {result.success_count || 0} device{result.success_count === 1 ? "" : "s"}.
              {result.failure_count > 0 && ` · ${result.failure_count} failed.`}
            </div>
          )}

          <button data-testid="push-send-btn" type="submit" disabled={sending} className="btn-gold" style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
            <Send size={14} /> {sending ? "Sending…" : "Send Notification"}
          </button>
        </form>

        {/* Customer context card */}
        {target !== "all" && (
          <div className="glass-card" data-testid="push-customer-context" style={{ padding: "18px", border: "1px solid rgba(255,209,102,0.18)", position: "sticky", top: "88px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#FFD166", fontWeight: 700, fontSize: "0.86rem", marginBottom: "12px" }}>
              <Users size={14} /> Customer Snapshot
            </div>
            {ctxLoading ? (
              <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.82rem" }}>Loading…</div>
            ) : context?.error ? (
              <div style={{ color: "#fca5a5", fontSize: "0.82rem" }}>{context.error}</div>
            ) : context ? (
              <>
                <div style={{ marginBottom: "12px" }}>
                  <div style={{ color: "#fff", fontSize: "0.95rem", fontWeight: 600 }}>{context.user?.name || "—"}</div>
                  <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.78rem" }}>{context.user?.email}</div>
                  {context.user?.phone && <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.78rem" }}>{context.user.phone}</div>}
                  <div style={{ marginTop: 6 }}>
                    <span style={{ background: "rgba(255,209,102,0.14)", color: "#FFD166", padding: "2px 8px", borderRadius: "999px", fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                      {context.user?.plan || "free"}
                    </span>
                    {context.push_tokens_registered > 0 ? (
                      <span style={{ marginLeft: 6, background: "rgba(34,197,94,0.14)", color: "#86efac", padding: "2px 8px", borderRadius: "999px", fontSize: "0.68rem", fontWeight: 700 }}>
                        {context.push_tokens_registered} device{context.push_tokens_registered === 1 ? "" : "s"}
                      </span>
                    ) : (
                      <span style={{ marginLeft: 6, background: "rgba(239,68,68,0.14)", color: "#fca5a5", padding: "2px 8px", borderRadius: "999px", fontSize: "0.68rem", fontWeight: 700 }}>
                        no push device
                      </span>
                    )}
                  </div>
                </div>

                {(birth.day || birth.city) && (
                  <div style={{ marginBottom: "12px", background: "rgba(0,0,0,0.25)", borderRadius: "8px", padding: "10px 12px" }}>
                    <div style={{ color: "#c4b5fd", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>Birth details</div>
                    {birth.day && (
                      <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "0.82rem" }}>
                        {birth.day} {monthName} {birth.year} · {String(birth.hour ?? "").padStart(2,"0")}:{String(birth.minute ?? "").padStart(2,"0")}
                      </div>
                    )}
                    {birth.city && <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.8rem" }}>{birth.city}</div>}
                  </div>
                )}

                {context.kundli_preview && (
                  <div style={{ marginBottom: "12px" }}>
                    <div style={{ color: "#c4b5fd", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>Kundli snippet</div>
                    <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.76rem", lineHeight: 1.5, maxHeight: "120px", overflowY: "auto", background: "rgba(0,0,0,0.25)", padding: "8px 10px", borderRadius: "8px" }}>
                      {context.kundli_preview}
                    </div>
                  </div>
                )}

                {(context.latest_chat?.question || context.latest_chat?.answer) && (
                  <div>
                    <div style={{ color: "#c4b5fd", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>Recent AI chat</div>
                    {context.latest_chat.question && (
                      <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.75rem", marginBottom: 4 }}>
                        <b style={{ color: "#FFD166" }}>Q:</b> {context.latest_chat.question}
                      </div>
                    )}
                    {context.latest_chat.answer && (
                      <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.75rem", lineHeight: 1.5, maxHeight: "100px", overflowY: "auto" }}>
                        <b style={{ color: "#86efac" }}>A:</b> {context.latest_chat.answer}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function EmailCampaignAnalytics() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/email/campaigns`, { withCredentials: true })
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]));
  }, []);
  if (rows === null) return <div style={{ color: "rgba(255,255,255,0.4)", padding: "20px" }}>Loading email analytics…</div>;
  if (!rows.length) return <div style={{ color: "rgba(255,255,255,0.4)", padding: "20px" }}>No email campaigns yet.</div>;
  const totals = rows.reduce((acc, r) => ({
    sent: acc.sent + (r.target_count || 0),
    delivered: acc.delivered + (r.success_count || 0),
    failed: acc.failed + (r.failure_count || 0),
  }), { sent: 0, delivered: 0, failed: 0 });
  const rate = totals.sent ? ((totals.delivered / totals.sent) * 100).toFixed(1) : "0.0";
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "12px", marginBottom: "20px" }}>
        {[
          { l: "Total campaigns", v: rows.length, c: "#FFD166" },
          { l: "Emails delivered", v: totals.delivered, c: "#86efac" },
          { l: "Delivery rate", v: `${rate}%`, c: "#c084fc" },
          { l: "Failed", v: totals.failed, c: "#fca5a5" },
        ].map(s => (
          <div key={s.l} style={{ background: `${s.c}11`, border: `1px solid ${s.c}33`, borderRadius: "12px", padding: "14px 16px" }}>
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>{s.l}</div>
            <div style={{ color: s.c, fontFamily: "'Cinzel', serif", fontSize: "1.4rem", fontWeight: 700 }}>{s.v}</div>
          </div>
        ))}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
          <thead>
            <tr style={{ color: "#c4b5fd", textAlign: "left", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
              <th style={{ padding: "8px 10px" }}>Sent</th>
              <th style={{ padding: "8px 10px" }}>Target</th>
              <th style={{ padding: "8px 10px" }}>Subject</th>
              <th style={{ padding: "8px 10px", textAlign: "right" }}>Delivered / Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`email-campaign-row-${r.id}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.75)" }}>
                <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}</td>
                <td style={{ padding: "8px 10px" }}>{r.target_type === "single" ? "1 user" : `Broadcast (${r.target_count})`}</td>
                <td style={{ padding: "8px 10px", color: "#FFD166", fontWeight: 600 }}>{r.subject}</td>
                <td style={{ padding: "8px 10px", textAlign: "right", color: r.failure_count ? "#fca5a5" : "#86efac" }}>
                  {r.success_count} / {r.target_count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PushBroadcastAnalytics() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/push/broadcasts`, { withCredentials: true })
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]));
  }, []);
  if (rows === null) return <div style={{ color: "rgba(255,255,255,0.4)", padding: "20px" }}>Loading push analytics…</div>;
  if (!rows.length) return <div style={{ color: "rgba(255,255,255,0.4)", padding: "20px" }}>No push broadcasts yet.</div>;
  const totals = rows.reduce((acc, r) => ({
    sent: acc.sent + (r.target_count || 0),
    delivered: acc.delivered + (r.success_count || 0),
    failed: acc.failed + (r.failure_count || 0),
    opens: acc.opens + (r.open_count || 0),
  }), { sent: 0, delivered: 0, failed: 0, opens: 0 });
  const deliveryRate = totals.sent ? ((totals.delivered / totals.sent) * 100).toFixed(1) : "0.0";
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "12px", marginBottom: "20px" }}>
        {[
          { l: "Total broadcasts", v: rows.length, c: "#FFD166" },
          { l: "Devices reached", v: totals.delivered, c: "#86efac" },
          { l: "Delivery rate", v: `${deliveryRate}%`, c: "#c084fc" },
          { l: "Failed", v: totals.failed, c: "#fca5a5" },
          { l: "Opens tracked", v: totals.opens, c: "#67e8f9" },
        ].map(s => (
          <div key={s.l} style={{ background: `${s.c}11`, border: `1px solid ${s.c}33`, borderRadius: "12px", padding: "14px 16px" }}>
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>{s.l}</div>
            <div style={{ color: s.c, fontFamily: "'Cinzel', serif", fontSize: "1.4rem", fontWeight: 700 }}>{s.v}</div>
          </div>
        ))}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
          <thead>
            <tr style={{ color: "#c4b5fd", textAlign: "left", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
              <th style={{ padding: "8px 10px" }}>Sent</th>
              <th style={{ padding: "8px 10px" }}>Target</th>
              <th style={{ padding: "8px 10px" }}>Title</th>
              <th style={{ padding: "8px 10px", textAlign: "right" }}>Delivered / Total</th>
              <th style={{ padding: "8px 10px", textAlign: "right" }}>Opens</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`push-broadcast-row-${r.id}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.75)" }}>
                <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}</td>
                <td style={{ padding: "8px 10px" }}>{r.target_type === "single" ? "1 customer" : `All (${r.target_count})`}</td>
                <td style={{ padding: "8px 10px", color: "#FFD166", fontWeight: 600 }}>{r.title}</td>
                <td style={{ padding: "8px 10px", textAlign: "right", color: r.failure_count ? "#fca5a5" : "#86efac" }}>
                  {r.success_count} / {r.target_count}
                </td>
                <td style={{ padding: "8px 10px", textAlign: "right", color: "#67e8f9" }}>{r.open_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color, sub }) {
  return (
    <div className="glass-card" style={{ padding: "20px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
        <div style={{ color, opacity: 0.8 }}>{icon}</div>
      </div>
      <div style={{ fontSize: "1.8rem", fontWeight: "700", color, fontFamily: "'Cinzel', serif" }}>{value}</div>
      <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.8rem" }}>{label}</div>
      {sub && <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.72rem", marginTop: "4px" }}>{sub}</div>}
    </div>
  );
}

function AvailabilityControl() {
  const [av, setAv] = useState(null);
  const [mins, setMins] = useState(10);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/astrologer-availability`, { withCredentials: true })
      .then(r => { setAv(r.data); setMins(r.data.reply_minutes); })
      .catch(() => { setAv({ online: true, reply_minutes: 10 }); });
  }, []);
  if (!av) return null;
  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/astrologer-availability`, next, { withCredentials: true });
      setAv(data); setMins(data.reply_minutes);
    } catch (e) { /* ignore */ } finally { setSaving(false); }
  };
  return (
    <div data-testid="admin-availability-control" className="glass-card" style={{ padding: "20px 24px", marginBottom: "24px", border: "1px solid rgba(52,211,153,0.2)" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
        <div>
          <h3 className="font-cinzel" style={{ color: "#FFD166", fontSize: "0.98rem", marginBottom: "4px" }}>Instant Chat — Astrologer Availability</h3>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.82rem" }}>Controls the online / reply-time badge customers see before paying for Instant Chat.</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "18px", flexWrap: "wrap" }}>
          <button data-testid="availability-toggle-btn" disabled={saving} onClick={() => save({ online: !av.online, reply_minutes: mins })}
            style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "9px 16px", borderRadius: "50px", cursor: "pointer",
              border: `1px solid ${av.online ? "rgba(52,211,153,0.5)" : "rgba(255,255,255,0.2)"}`,
              background: av.online ? "rgba(52,211,153,0.12)" : "rgba(255,255,255,0.04)", color: av.online ? "#6EE7B7" : "rgba(255,255,255,0.6)", fontWeight: 700, fontSize: "0.85rem" }}>
            <span style={{ width: "9px", height: "9px", borderRadius: "50%", background: av.online ? "#34D399" : "#9CA3AF" }} />
            {av.online ? "Online" : "Away"}
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.82rem" }}>Replies in ~</span>
            <input data-testid="availability-minutes-input" type="number" min={1} max={240} value={mins} onChange={e => setMins(Number(e.target.value))}
              style={{ width: "64px", padding: "7px 8px", borderRadius: "8px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", color: "white", textAlign: "center" }} />
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.82rem" }}>min</span>
            <button data-testid="availability-save-btn" disabled={saving} onClick={() => save({ online: av.online, reply_minutes: mins })} className="btn-gold" style={{ padding: "7px 16px", fontSize: "0.82rem" }}>{saving ? "…" : "Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UsdRateControl({ rate, onSaved }) {
  const [value, setValue] = useState(rate || 88);
  const [saving, setSaving] = useState(false);
  useEffect(() => setValue(rate || 88), [rate]);
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/usd-rate`, { rate: Number(value) }, { withCredentials: true });
      onSaved?.();
    } catch (e) { /* ignore */ } finally { setSaving(false); }
  };
  return (
    <div data-testid="admin-usd-rate-control" style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
      <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.78rem" }}>₹ per $</span>
      <input data-testid="usd-rate-input" type="number" min={1} max={1000} step="0.5" value={value} onChange={e => setValue(e.target.value)}
        style={{ width: "80px", padding: "6px 8px", borderRadius: "8px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", color: "white", textAlign: "center" }} />
      <button data-testid="usd-rate-save-btn" disabled={saving} onClick={save} className="btn-gold" style={{ padding: "6px 14px", fontSize: "0.78rem" }}>{saving ? "…" : "Save"}</button>
    </div>
  );
}

const PROVIDER_META = {
  cashfree: { label: "Cashfree · UPI / Card", color: "#FFD166" },
  paypal: { label: "PayPal · International", color: "#67e8f9" },
  google_play: { label: "Google Play Billing", color: "#86efac" },
};

function ProviderBreakdown({ analytics, onRateSaved }) {
  const rows = analytics.by_provider || [];
  return (
    <div data-testid="admin-provider-breakdown" className="glass-card" style={{ padding: "24px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
        <h3 className="font-cinzel" style={{ color: "#FFD166", fontSize: "0.95rem" }}>PayPal vs UPI earnings</h3>
        <UsdRateControl rate={analytics.usd_inr_rate} onSaved={onRateSaved} />
      </div>
      {rows.length === 0 ? (
        <p data-testid="admin-provider-empty" style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.85rem" }}>No successful payments yet.</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
          {rows.map(r => {
            const meta = PROVIDER_META[r.provider] || { label: r.provider, color: "#c084fc" };
            return (
              <div key={r.provider} data-testid={`provider-card-${r.provider}`} style={{ background: "rgba(255,255,255,0.03)", borderRadius: "12px", padding: "16px", border: `1px solid ${meta.color}25` }}>
                <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.78rem", marginBottom: "6px" }}>{meta.label}</div>
                <div style={{ color: meta.color, fontWeight: 700, fontSize: "1.35rem" }}>{fmtPaid(r.amount, r.currency)}</div>
                <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem", marginTop: "4px" }}>
                  {r.count} transaction{r.count === 1 ? "" : "s"}
                  {r.currency === "USD" && ` · ≈ ${fmtInr(r.inr_equivalent)}`}
                </div>
              </div>
            );
          })}
          <div data-testid="provider-card-combined" style={{ background: "linear-gradient(135deg, rgba(134,239,172,0.12), rgba(134,239,172,0.02))", borderRadius: "12px", padding: "16px", border: "1px solid rgba(134,239,172,0.25)" }}>
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.78rem", marginBottom: "6px" }}>Combined (₹ equivalent)</div>
            <div style={{ color: "#86efac", fontWeight: 700, fontSize: "1.35rem" }}>{fmtInr(analytics.total_revenue_inr || 0)}</div>
            <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem", marginTop: "4px" }}>at ₹{analytics.usd_inr_rate} per $</div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatBirth(bd) {
  if (!bd || !bd.day) return "—";
  const mon = bd.month ? new Date(2000, bd.month - 1).toLocaleString("en-US", { month: "short" }) : "";
  let s = `${bd.day} ${mon} ${bd.year || ""}`.trim();
  if (bd.hour != null && bd.minute != null) {
    const period = bd.hour < 12 ? "AM" : "PM";
    const h12 = ((bd.hour + 11) % 12) + 1;
    s += ` · ${String(h12).padStart(2, "0")}:${String(bd.minute).padStart(2, "0")} ${period}`;
  }
  if (bd.city) s += ` · ${bd.city}`;
  return s;
}

function CustomerDetailContent({ detail }) {
  const u = detail.user || {};
  const s = detail.stats || {};
  return (
    <div>
      {/* Identity */}
      <div style={{ marginBottom: "18px", padding: "16px", background: "rgba(255,209,102,0.06)", borderRadius: "12px", border: "1px solid rgba(255,209,102,0.15)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px", flexWrap: "wrap" }}>
          <div style={{ width: "42px", height: "42px", borderRadius: "50%", background: "linear-gradient(135deg, #9D4EDD, #5B1E8C)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
            {(u.name || u.email || "?")[0].toUpperCase()}
          </div>
          <div>
            <div style={{ color: "white", fontWeight: 700, fontSize: "1.02rem" }}>{u.name || "—"}</div>
            <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.82rem" }}>{u.email} · {u.phone || "no phone"}</div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "10px" }}>
          <Mini label="Current Plan" value={(s.current_plan || "free").replace("_", " ")} color="#FFD166" />
          <Mini label="Kundlis" value={s.kundlis || 0} color="#c084fc" />
          <Mini label="Consultations" value={s.consultations || 0} color="#86efac" />
          <Mini label="Successful Payments" value={s.successful_payments || 0} color="#67e8f9" />
          <Mini label="Lifetime Spend" value={`₹${s.lifetime_spend || 0}`} color="#FFD166" />
          <Mini label="Signed up" value={u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN") : "—"} color="#A89CB8" />
        </div>
        {detail.kundlis?.[0]?.birth_details?.day && (
          <div data-testid="customer-birth-details" style={{ marginTop: "12px", padding: "10px 12px", background: "rgba(157,78,221,0.08)", borderRadius: "10px", border: "1px solid rgba(157,78,221,0.18)" }}>
            <span style={{ color: "#c4b5fd", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Birth Details</span>
            <div style={{ color: "white", fontSize: "0.86rem", marginTop: "3px" }}>{formatBirth(detail.kundlis[0].birth_details)}</div>
          </div>
        )}
      </div>

      {/* Kundlis */}
      <DetailList title={`Kundli Reports (${(detail.kundlis || []).length})`} color="#FFD166" empty="No kundlis generated.">
        {(detail.kundlis || []).map(k => (
          <div key={k.id} style={{ padding: "10px 12px", background: "rgba(255,255,255,0.03)", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.05)" }}>
            <div style={{ color: "white", fontWeight: 600, fontSize: "0.88rem" }}>{k.name || "—"}</div>
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.76rem" }}>
              {formatBirth(k.birth_details)}
            </div>
            <div style={{ color: "rgba(255,255,255,0.32)", fontSize: "0.72rem" }}>
              {k.created_at ? new Date(k.created_at).toLocaleString("en-IN") : "—"}
            </div>
          </div>
        ))}
      </DetailList>

      {/* Consultations */}
      <DetailList title={`Consultations (${(detail.consultations || []).length})`} color="#86efac" empty="No consultations booked.">
        {(detail.consultations || []).map(c => (
          <div key={c.id} style={{ padding: "10px 12px", background: "rgba(255,255,255,0.03)", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.05)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
              <div style={{ color: "white", fontWeight: 600, fontSize: "0.86rem" }}>{c.date} · {c.time_slot}</div>
              <span style={{ background: "rgba(255,209,102,0.15)", color: "#FFD166", padding: "1px 8px", borderRadius: "20px", fontSize: "0.68rem", fontWeight: 700 }}>#{c.booking_ref}</span>
            </div>
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.76rem", marginTop: "2px" }}>{c.concern}</div>
            <div style={{ color: "rgba(255,255,255,0.32)", fontSize: "0.7rem", marginTop: "2px" }}>Status: {c.status}</div>
          </div>
        ))}
      </DetailList>

      {/* Payments */}
      <DetailList title={`Payments (${(detail.payments || []).length})`} color="#67e8f9" empty="No payments.">
        {(detail.payments || []).map(p => (
          <div key={p.id} style={{ display: "flex", justifyContent: "space-between", padding: "10px 12px", background: "rgba(255,255,255,0.03)", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.05)", gap: "8px", flexWrap: "wrap" }}>
            <div>
              <div style={{ color: "white", fontWeight: 600, textTransform: "capitalize", fontSize: "0.86rem" }}>{(p.plan || "").replace("_", " ")}</div>
              <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.72rem" }}>{p.created_at ? new Date(p.created_at).toLocaleString("en-IN") : "—"}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ color: "#FFD166", fontWeight: 700 }}>₹{p.amount}</span>
              <span style={{ background: p.status === "success" ? "rgba(34,197,94,0.18)" : "rgba(107,114,128,0.15)", color: p.status === "success" ? "#86efac" : "#9ca3af", padding: "1px 8px", borderRadius: "20px", fontSize: "0.7rem", fontWeight: 600 }}>{p.status}</span>
            </div>
          </div>
        ))}
      </DetailList>

      {/* Live chats */}
      <DetailList title={`Live Chat Sessions (${(detail.live_chats || []).length})`} color="#c084fc" empty="No live chats.">
        {(detail.live_chats || []).map(lc => (
          <Link key={lc.id} to={`/livechat/${lc.id}`} style={{ display: "block", padding: "10px 12px", background: "rgba(255,255,255,0.03)", borderRadius: "10px", border: "1px solid rgba(157,78,221,0.18)", textDecoration: "none", color: "rgba(255,255,255,0.9)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "6px" }}>
              <span style={{ color: "#FFD166", fontWeight: 600, fontSize: "0.82rem" }}>#{lc.booking_ref}</span>
              <span style={{ background: lc.status === "closed" ? "rgba(239,68,68,0.15)" : "rgba(34,197,94,0.18)", color: lc.status === "closed" ? "#fca5a5" : "#86efac", padding: "1px 8px", borderRadius: "20px", fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase" }}>{lc.status}</span>
            </div>
            <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.74rem", marginTop: "3px" }}>{lc.concern || "—"}</div>
          </Link>
        ))}
      </DetailList>
    </div>
  );
}

function Mini({ label, value, color }) {
  return (
    <div style={{ padding: "8px 10px", background: "rgba(0,0,0,0.18)", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.05)" }}>
      <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "2px" }}>{label}</div>
      <div style={{ color, fontWeight: 700, fontSize: "0.9rem", textTransform: "capitalize" }}>{value}</div>
    </div>
  );
}

function DetailList({ title, color, empty, children }) {
  const items = React.Children.toArray(children);
  return (
    <div style={{ marginBottom: "16px" }}>
      <h4 className="font-cinzel" style={{ color, fontSize: "0.92rem", marginBottom: "8px", letterSpacing: "0.3px" }}>{title}</h4>
      {items.length === 0 ? (
        <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.8rem", fontStyle: "italic", padding: "6px 4px" }}>{empty}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "200px", overflowY: "auto", paddingRight: "4px" }}>{items}</div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [liveChats, setLiveChats] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [tab, setTab] = useState("overview");
  const [providerFilter, setProviderFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/analytics`, { withCredentials: true }),
      axios.get(`${API}/admin/users`, { withCredentials: true }),
      axios.get(`${API}/admin/payments`, { withCredentials: true }),
      axios.get(`${API}/admin/consultations`, { withCredentials: true }),
      axios.get(`${API}/admin/livechats`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/admin/customers`, { withCredentials: true }).catch(() => ({ data: { customers: [] } })),
      // Site analytics (visitors, top pages, referrers) — feeds the Analytics tab.
      axios.get(`${API}/admin/analytics/dashboard`, { withCredentials: true }).catch(() => ({ data: null })),
    ]).then(([a, u, p, c, lc, cu, ad]) => {
      setAnalytics(a.data);
      setUsers(u.data.users || []);
      setPayments(p.data || []);
      setConsultations(c.data || []);
      setLiveChats(lc.data || []);
      setCustomers(cu.data.customers || []);
      setAnalyticsData(ad.data || null);
    }).catch(() => {}).finally(() => setLoading(false));

    // Auto-refresh live chats every 12 s so the admin sees new customer messages
    const id = setInterval(() => {
      axios.get(`${API}/admin/livechats`, { withCredentials: true })
        .then(res => setLiveChats(res.data || []))
        .catch(() => {});
    }, 12000);
    return () => clearInterval(id);
  }, []);

  const visiblePayments = useMemo(
    () => (providerFilter === "all" ? payments : payments.filter(p => (p.provider || "cashfree") === providerFilter)),
    [payments, providerFilter]
  );

  const exportPaymentsCsv = () => {
    const head = ["Order ID", "Customer", "Email", "Phone", "Plan", "Amount", "Currency", "Provider", "Status", "Date"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = visiblePayments.map(p => [
      p.order_id, p.customer_name, p.customer_email, p.customer_phone, p.plan,
      p.amount, (p.currency || "INR"), (p.provider || "cashfree"), p.status, (p.created_at || "").slice(0, 19),
    ].map(esc).join(","));
    const blob = new Blob([[head.map(esc).join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `astrofate-payments-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const refreshAnalytics = () => {
    axios.get(`${API}/admin/analytics`, { withCredentials: true })
      .then(r => setAnalytics(r.data))
      .catch(() => {});
  };

  // Trigger the once-a-day analytics email digest on-demand for the admin.
  const sendDigestEmail = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/analytics/email-digest`, { withCredentials: true });
      alert(data?.message || "Digest email sent.");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to send digest.");
    }
  };

  const totalUnread = useMemo(
    () => liveChats.reduce((acc, lc) => acc + (lc.unread || 0), 0),
    [liveChats]
  );

  const updateConsultationStatus = async (id, status) => {
    await axios.patch(`${API}/admin/consultations/${id}`, { status }, { withCredentials: true });
    setConsultations(prev => prev.map(c => c.id === id ? {...c, status} : c));
  };

  const openCustomer = async (id) => {
    setSelectedCustomer(id); setCustomerDetail(null);
    try {
      const res = await axios.get(`${API}/admin/customers/${id}`, { withCredentials: true });
      setCustomerDetail(res.data);
    } catch (e) {
      setCustomerDetail({ error: "Could not load customer" });
    }
  };

  const closeChatSession = async (chatId) => {
    if (!window.confirm("Close this live chat session?")) return;
    try {
      await axios.post(`${API}/livechat/${chatId}/close`, {}, { withCredentials: true });
      setLiveChats(prev => prev.map(lc => lc.chat_id === chatId ? {...lc, status: "closed"} : lc));
    } catch (e) {
      alert(e.response?.data?.detail || "Could not close chat");
    }
  };

  const openAdminChat = (chatId) => {
    // Optimistically clear unread, then notify backend
    setLiveChats(prev => prev.map(lc => lc.chat_id === chatId ? {...lc, unread: 0} : lc));
    axios.post(`${API}/admin/livechats/${chatId}/mark-read`, {}, { withCredentials: true }).catch(() => {});
  };

  const downloadCustomersCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/customers/export.csv`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `astrofate-customers-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("Export failed. Please try again.");
    }
  };

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(c =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q)
    );
  }, [customers, customerSearch]);

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#120021", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="cosmic-spinner" />
    </div>
  );

  const tabs = ["overview", "analytics", "android", "customers", "users", "payments", "consultations", "live chats", "testimonials", "push", "whatsapp", "horoscope", "email", "weekly digest"];

  return (
    <div style={{ minHeight: "100vh", background: "#120021", paddingTop: "80px" }}>
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", width: "500px", height: "500px", background: "radial-gradient(circle, rgba(91,30,140,0.15), transparent)", top: 0, right: 0, borderRadius: "50%" }} />
      </div>

      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 16px 60px", position: "relative", zIndex: 1 }}>
        <NotificationOptIn userRole="admin" />
        {/* Header */}
        <div className="animate-fadeInUp" style={{ marginBottom: "28px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Star size={24} style={{ color: "#FFD166" }} fill="#FFD166" />
            <h1 className="font-cinzel" style={{ fontSize: "1.8rem", color: "#FFD166" }}>Admin Dashboard</h1>
          </div>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.85rem", marginTop: "4px" }}>AstroFate Control Center</p>
        </div>

        {/* OTP gate — nothing below renders until the admin verifies via
            email code sent to info.astrofate@gmail.com. Renews every 30min. */}
        <AdminOTPGate>

        {/* Tabs */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "24px", borderBottom: "1px solid rgba(255,255,255,0.08)", paddingBottom: "4px" }}>
          {tabs.map(t => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
              style={{ padding: "10px 20px", border: "none", cursor: "pointer", borderRadius: "8px 8px 0 0", textTransform: "capitalize",
                background: tab === t ? "rgba(255,209,102,0.1)" : "transparent",
                color: tab === t ? "#FFD166" : "rgba(255,255,255,0.4)",
                fontWeight: tab === t ? "700" : "500",
                fontFamily: "'DM Sans', sans-serif", fontSize: "0.88rem",
                borderBottom: tab === t ? "2px solid #FFD166" : "2px solid transparent",
                display: "inline-flex", alignItems: "center", gap: "6px",
              }}>
              {t === "android" ? "Android App" : t === "whatsapp" ? "WhatsApp" : t}
              {t === "live chats" && totalUnread > 0 && (
                <span data-testid="admin-tab-unread-badge" style={{ background: "linear-gradient(135deg, #ef4444, #f97316)", color: "white", fontSize: "0.66rem", fontWeight: 700, padding: "1px 7px", borderRadius: "10px", lineHeight: 1.4, animation: "pulse-badge 1.6s ease-in-out infinite" }}>
                  {totalUnread > 99 ? "99+" : totalUnread}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === "android" && <AndroidAdmin />}
        {tab === "testimonials" && (
          <div className="glass-card" style={{ padding: "24px" }}>
            <TestimonialsAdmin />
          </div>
        )}
        {/* Overview */}
        {tab === "overview" && analytics && (
          <div>
            <AvailabilityControl />
            <div data-testid="admin-stats" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "28px" }}>
              <StatCard icon={<Users size={20} />} label="Total Users" value={analytics.total_users} color="#FFD166" />
              <StatCard icon={<TrendingUp size={20} />} label="Revenue (₹ equiv.)" value={fmtInr(analytics.total_revenue_inr ?? analytics.total_revenue)} color="#86efac" sub={`${analytics.ai_chat_sales} AI + ${analytics.consultation_sales} Consult`} />
              <StatCard icon={<Star size={20} />} label="Reports Generated" value={analytics.total_reports} color="#9D4EDD" />
              <StatCard icon={<Calendar size={20} />} label="Consultations" value={analytics.total_consultations} color="#67e8f9" />
            </div>
            <div style={{ marginBottom: "20px" }}>
              <ProviderBreakdown analytics={analytics} onRateSaved={refreshAnalytics} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
              <div className="glass-card" style={{ padding: "24px" }}>
                <h3 className="font-cinzel" style={{ color: "#FFD166", marginBottom: "16px", fontSize: "0.95rem" }}>Revenue Breakdown</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {[
                    { label: "AI Chat", value: analytics.ai_chat_sales, color: "#FFD166" },
                    { label: "Consultation", value: analytics.consultation_sales, color: "#9D4EDD" },
                    { label: "Match report", value: analytics.match_sales || 0, color: "#f9a8d4" }
                  ].map(item => (
                    <div key={item.label} style={{ background: "rgba(255,255,255,0.03)", borderRadius: "10px", padding: "14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem" }}>{item.label}</span>
                        <span style={{ color: item.color, fontWeight: "700" }}>{item.value} sold</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="glass-card" style={{ padding: "24px" }}>
                <h3 className="font-cinzel" style={{ color: "#FFD166", marginBottom: "16px", fontSize: "0.95rem" }}>Quick Stats</h3>
                {[
                  { label: "Total AI Messages", value: analytics.total_chats },
                  { label: "Avg Revenue/User", value: analytics.total_users ? fmtInr((analytics.total_revenue_inr ?? analytics.total_revenue) / analytics.total_users) : "₹0" },
                  { label: "Reports/User", value: analytics.total_users ? (analytics.total_reports / analytics.total_users).toFixed(1) : 0 }
                ].map(item => (
                  <div key={item.label} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem" }}>{item.label}</span>
                    <span style={{ color: "white", fontWeight: "600" }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Users */}
        {tab === "users" && (
          <div data-testid="admin-users-table" className="glass-card" style={{ padding: "24px", overflowX: "auto" }}>
            <h3 className="font-cinzel" style={{ color: "#FFD166", marginBottom: "20px" }}>Users ({users.length})</h3>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr>
                  {["Name", "Email", "Role", "Plan", "Birth Details", "Joined"].map(h => (
                    <th key={h} style={{ padding: "10px 12px", textAlign: "left", color: "#FFD166", background: "rgba(157,78,221,0.1)", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u.id} data-testid={`admin-user-${i}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    <td style={{ padding: "10px 12px", color: "white" }}>{u.name}</td>
                    <td style={{ padding: "10px 12px", color: "#A89CB8" }}>{u.email}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ background: u.role === "admin" ? "rgba(255,209,102,0.15)" : "rgba(157,78,221,0.15)", color: u.role === "admin" ? "#FFD166" : "#c084fc", padding: "2px 8px", borderRadius: "20px", fontSize: "0.72rem" }}>{u.role}</span>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ background: u.plan !== "free" ? "rgba(34,197,94,0.15)" : "rgba(107,114,128,0.1)", color: u.plan !== "free" ? "#86efac" : "#9ca3af", padding: "2px 8px", borderRadius: "20px", fontSize: "0.72rem", textTransform: "capitalize" }}>{u.plan}</span>
                    </td>
                    <td data-testid={`admin-user-birth-${i}`} style={{ padding: "10px 12px", color: "#c4b5fd", fontSize: "0.78rem" }}>{formatBirth(u.birth_details)}</td>
                    <td style={{ padding: "10px 12px", color: "#A89CB8", fontSize: "0.8rem" }}>{u.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Payments */}
        {tab === "payments" && (
          <div data-testid="admin-payments-table" className="glass-card" style={{ padding: "24px", overflowX: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", marginBottom: "20px" }}>
              <h3 className="font-cinzel" style={{ color: "#FFD166" }}>Payments ({visiblePayments.length})</h3>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <select data-testid="payments-provider-filter" value={providerFilter} onChange={e => setProviderFilter(e.target.value)}
                  style={{ padding: "7px 10px", borderRadius: "8px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", color: "white", fontSize: "0.82rem" }}>
                  <option value="all">All providers</option>
                  <option value="cashfree">Cashfree · UPI / Card</option>
                  <option value="paypal">PayPal · International</option>
                  <option value="google_play">Google Play</option>
                </select>
                <button data-testid="payments-export-csv" onClick={exportPaymentsCsv} className="btn-gold" style={{ padding: "7px 16px", fontSize: "0.82rem" }}>
                  <Download size={13} /> CSV
                </button>
              </div>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem", minWidth: "940px" }}>
              <thead>
                <tr>
                  {["Customer", "Plan", "Amount", "Paid via", "Status", "Platform", "Date"].map(h => (
                    <th key={h} style={{ padding: "10px 12px", textAlign: "left", color: "#FFD166", background: "rgba(157,78,221,0.1)", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visiblePayments.map((p, i) => (
                  <tr key={p.id} data-testid={`admin-payment-${i}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    <td style={{ padding: "10px 12px", color: "#E2D8F0", minWidth: "220px" }}>
                      <div style={{ fontWeight: 600 }}>{p.customer_name || "—"}</div>
                      <div style={{ color: "#A89CB8", fontSize: "0.75rem" }}>{p.customer_email}</div>
                      {p.customer_phone && <div style={{ color: "#A89CB8", fontSize: "0.75rem" }}>📱 {p.customer_phone}</div>}
                      <div style={{ color: "#A89CB8", fontSize: "0.68rem", marginTop: 2 }}>#{(p.order_id || "").slice(0, 18)}…</div>
                    </td>
                    <td style={{ padding: "10px 12px", color: "#E2D8F0", textTransform: "capitalize" }}>{p.plan?.replace("_", " ")}</td>
                    <td style={{ padding: "10px 12px", color: "#FFD166", fontWeight: "700" }}>{fmtPaid(p.amount, p.currency)}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{
                        padding: "2px 9px", borderRadius: "999px", fontSize: "0.72rem", fontWeight: 700,
                        background: (p.provider === "paypal") ? "rgba(103,232,249,0.14)" : (p.provider === "google_play") ? "rgba(134,239,172,0.14)" : "rgba(255,209,102,0.14)",
                        color: (p.provider === "paypal") ? "#67e8f9" : (p.provider === "google_play") ? "#86efac" : "#FFD166",
                      }}>{(p.provider || "cashfree").replace("_", " ")}</span>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ background: p.status === "success" ? "rgba(34,197,94,0.15)" : "rgba(249,115,22,0.15)", color: p.status === "success" ? "#86efac" : "#fdba74", padding: "2px 8px", borderRadius: "20px", fontSize: "0.72rem" }}>{p.status}</span>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {(p.customer_platforms || []).length === 0 ? (
                        <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.75rem" }}>web</span>
                      ) : (
                        (p.customer_platforms || []).map(pl => (
                          <span key={pl} style={{
                            display: "inline-block", marginRight: 4, padding: "2px 8px", borderRadius: "999px",
                            background: pl === "android" ? "rgba(134,239,172,0.14)" : pl === "ios" ? "rgba(103,232,249,0.14)" : "rgba(255,209,102,0.14)",
                            color: pl === "android" ? "#86efac" : pl === "ios" ? "#67e8f9" : "#FFD166",
                            fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px",
                          }}>{pl === "android" ? "APK" : pl}</span>
                        ))
                      )}
                    </td>
                    <td style={{ padding: "10px 12px", color: "#A89CB8", fontSize: "0.8rem" }}>{p.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Consultations */}
        {tab === "consultations" && (
          <div data-testid="admin-consultations-table" className="glass-card" style={{ padding: "24px", overflowX: "auto" }}>
            <h3 className="font-cinzel" style={{ color: "#FFD166", marginBottom: "20px" }}>Consultations ({consultations.length})</h3>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr>
                  {["User", "Date", "Time", "Concern", "Status", "Action"].map(h => (
                    <th key={h} style={{ padding: "10px 12px", textAlign: "left", color: "#FFD166", background: "rgba(157,78,221,0.1)", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {consultations.map((c, i) => (
                  <tr key={c.id} data-testid={`admin-consultation-${i}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    <td style={{ padding: "10px 12px", color: "white" }}>{c.user_name}</td>
                    <td style={{ padding: "10px 12px", color: "#E2D8F0" }}>{c.date}</td>
                    <td style={{ padding: "10px 12px", color: "#A89CB8" }}>{c.time_slot}</td>
                    <td style={{ padding: "10px 12px", color: "#A89CB8", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.concern}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ background: c.status === "confirmed" ? "rgba(34,197,94,0.15)" : "rgba(249,115,22,0.15)", color: c.status === "confirmed" ? "#86efac" : "#fdba74", padding: "2px 8px", borderRadius: "20px", fontSize: "0.72rem" }}>{c.status}</span>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <select value={c.status} onChange={e => updateConsultationStatus(c.id, e.target.value)}
                        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "6px", padding: "4px 8px", color: "white", fontSize: "0.78rem", cursor: "pointer" }}>
                        {["pending", "confirmed", "completed", "cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Live Chats */}
        {tab === "live chats" && (
          <div className="glass-card" style={{ padding: "20px" }}>
            <h2 className="font-cinzel" style={{ color: "#FFD166", marginBottom: "14px", fontSize: "1.1rem", display: "flex", alignItems: "center", gap: "8px" }}>
              <MessageCircle size={18} /> Live Chats
            </h2>
            {liveChats.length === 0 ? (
              <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.88rem", padding: "20px 0", textAlign: "center" }}>
                No live chats yet. They appear automatically when a customer pays for a consultation.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {liveChats.map((lc, i) => {
                  const isClosed = lc.status === "closed";
                  const hasUnread = (lc.unread || 0) > 0;
                  return (
                    <div key={lc.chat_id} data-testid={`admin-livechat-${i}`} style={{
                      background: hasUnread ? "linear-gradient(135deg, rgba(239,68,68,0.08), rgba(157,78,221,0.06))" : "rgba(255,255,255,0.04)",
                      border: `1px solid ${isClosed ? "rgba(239,68,68,0.18)" : (hasUnread ? "rgba(239,68,68,0.45)" : "rgba(157,78,221,0.18)")}`,
                      borderRadius: "12px",
                      padding: "14px 16px",
                      position: "relative",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "6px", gap: "10px", flexWrap: "wrap" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                          <span style={{ fontWeight: 700, color: "white" }}>{lc.user_name || "Guest"}</span>
                          <span style={{ color: "#FFD166", fontSize: "0.74rem", fontWeight: 600 }}>#{lc.booking_ref}</span>
                          <span style={{
                            background: isClosed ? "rgba(239,68,68,0.15)" : "rgba(34,197,94,0.15)",
                            color: isClosed ? "#fca5a5" : "#86efac",
                            padding: "2px 9px", borderRadius: "20px", fontSize: "0.68rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px"
                          }}>{isClosed ? "Closed" : "Active"}</span>
                          {hasUnread && (
                            <span data-testid={`admin-livechat-unread-${i}`} style={{ background: "linear-gradient(135deg, #ef4444, #f97316)", color: "white", padding: "2px 9px", borderRadius: "20px", fontSize: "0.68rem", fontWeight: 700, animation: "pulse-badge 1.6s ease-in-out infinite" }}>
                              {lc.unread} new
                            </span>
                          )}
                        </div>
                        <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.72rem" }}>{lc.last_ts ? new Date(lc.last_ts).toLocaleString() : new Date(lc.created_at).toLocaleString()}</span>
                      </div>
                      <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.78rem", marginBottom: "4px" }}>
                        {lc.user_email} · {lc.user_phone || "no phone"} · <span style={{ color: "#c084fc" }}>{lc.concern}</span>
                      </div>
                      {lc.last_message && <div style={{ color: hasUnread ? "white" : "rgba(255,255,255,0.75)", fontSize: "0.85rem", fontStyle: "italic", marginBottom: "10px" }}>
                        <span style={{ color: lc.last_sender === "customer" ? "#c084fc" : "#FFD166", fontStyle: "normal", fontWeight: 600 }}>{lc.last_sender === "customer" ? "Customer" : "You"}:</span> &ldquo;{lc.last_message}&rdquo;
                      </div>}
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <Link to={`/livechat/${lc.chat_id}`} onClick={() => openAdminChat(lc.chat_id)} className="btn-gold" style={{ padding: "6px 14px", fontSize: "0.76rem", textDecoration: "none" }}>
                          {isClosed ? "View Transcript" : (hasUnread ? "Reply Now →" : "Open Chat")}
                        </Link>
                        {!isClosed && (
                          <button data-testid={`admin-livechat-close-${i}`} onClick={() => closeChatSession(lc.chat_id)} className="btn-ghost" style={{ padding: "6px 14px", fontSize: "0.76rem", color: "#fca5a5", borderColor: "rgba(239,68,68,0.3)" }}>
                            Close Session
                          </button>
                        )}
                        <LiveChatPush chat={lc} index={i} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Customers tab — searchable, exportable */}
        {tab === "analytics" && (
          <div className="glass-card" style={{ padding: "22px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "18px", flexWrap: "wrap", gap: "10px" }}>
              <h2 className="font-cinzel" style={{ color: "#FFD166", fontSize: "1.1rem", display: "flex", alignItems: "center", gap: "8px" }}>
                <TrendingUp size={18} /> Site Analytics
              </h2>
              <button data-testid="send-digest-btn" onClick={sendDigestEmail} className="btn-ghost" style={{ padding: "7px 14px", fontSize: "0.78rem" }}>
                Email me today&apos;s digest
              </button>
            </div>
            {!analyticsData ? (
              <div style={{ color: "rgba(255,255,255,0.45)", padding: "30px", textAlign: "center" }}>Loading…</div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "12px", marginBottom: "20px" }}>
                  {[
                    { k: "total_visitors", l: "Total visitors", c: "#FFD166" },
                    { k: "visitors_today", l: "Visitors today", c: "#86efac" },
                    { k: "total_users", l: "Registered users", c: "#c084fc" },
                    { k: "new_users_today", l: "New signups today", c: "#67e8f9" },
                  ].map(s => (
                    <div key={s.k} style={{ background: `${s.c}11`, border: `1px solid ${s.c}33`, borderRadius: "12px", padding: "14px 16px" }}>
                      <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>{s.l}</div>
                      <div style={{ color: s.c, fontFamily: "'Cinzel', serif", fontSize: "1.7rem", fontWeight: 700 }}>{analyticsData[s.k] || 0}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
                  <div style={{ background: "rgba(255,209,102,0.05)", borderRadius: "12px", padding: "16px", border: "1px solid rgba(255,209,102,0.15)" }}>
                    <h3 style={{ color: "#FFD166", fontSize: "0.86rem", marginBottom: "10px", fontFamily: "'Cinzel', serif" }}>Top Pages (30d)</h3>
                    {(analyticsData.top_pages || []).length === 0 ? <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.82rem" }}>No data yet</p> :
                      (analyticsData.top_pages || []).map((p, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                          <span style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.82rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>{p.path}</span>
                          <span style={{ color: "#FFD166", fontWeight: 700, fontSize: "0.84rem" }}>{p.views}</span>
                        </div>
                      ))
                    }
                  </div>
                  <div style={{ background: "rgba(134,239,172,0.05)", borderRadius: "12px", padding: "16px", border: "1px solid rgba(134,239,172,0.15)" }}>
                    <h3 style={{ color: "#86efac", fontSize: "0.86rem", marginBottom: "10px", fontFamily: "'Cinzel', serif" }}>Traffic Sources (30d)</h3>
                    {(analyticsData.sources || []).length === 0 ? <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.82rem" }}>No data yet</p> :
                      (analyticsData.sources || []).map((s, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                          <span style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.82rem", textTransform: "capitalize" }}>{s.source}</span>
                          <span style={{ color: "#86efac", fontWeight: 700, fontSize: "0.84rem" }}>{s.count}</span>
                        </div>
                      ))
                    }
                  </div>
                  <div style={{ background: "rgba(157,78,221,0.05)", borderRadius: "12px", padding: "16px", border: "1px solid rgba(157,78,221,0.15)" }}>
                    <h3 style={{ color: "#c084fc", fontSize: "0.86rem", marginBottom: "10px", fontFamily: "'Cinzel', serif" }}>Daily Signups (30d)</h3>
                    {(analyticsData.daily_signups || []).length === 0 ? <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.82rem" }}>No signups yet</p> :
                      (analyticsData.daily_signups || []).slice(-10).map((d, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                          <span style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.82rem" }}>{d.day}</span>
                          <span style={{ color: "#c084fc", fontWeight: 700, fontSize: "0.84rem" }}>+{d.count}</span>
                        </div>
                      ))
                    }
                  </div>
                </div>
                <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.7rem", marginTop: "16px", textAlign: "center" }}>
                  In-house analytics · GA4 also captures detailed funnels. Tip: trigger the daily digest via cron with `?key=DIGEST_KEY`.
                </p>

                {/* Push notification analytics */}
                <div style={{ marginTop: "28px", paddingTop: "20px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                  <h3 className="font-cinzel" style={{ color: "#FFD166", fontSize: "1rem", marginBottom: "14px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <Bell size={16} /> Push Notification Analytics
                  </h3>
                  <PushBroadcastAnalytics />
                </div>

                {/* Email campaign analytics */}
                <div style={{ marginTop: "28px", paddingTop: "20px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                  <h3 className="font-cinzel" style={{ color: "#FFD166", fontSize: "1rem", marginBottom: "14px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <Mail size={16} /> Email Campaign Analytics
                  </h3>
                  <EmailCampaignAnalytics />
                </div>
              </>
            )}
          </div>
        )}

        {/* Customers tab — searchable, exportable */}
        {tab === "customers" && (
          <div className="glass-card" style={{ padding: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px", flexWrap: "wrap", gap: "10px" }}>
              <h2 className="font-cinzel" style={{ color: "#FFD166", fontSize: "1.1rem", display: "flex", alignItems: "center", gap: "8px" }}>
                <Users size={18} /> Customers <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem", fontWeight: 500 }}>({customers.length})</span>
              </h2>
              <button data-testid="customers-export-csv-btn" onClick={downloadCustomersCsv} className="btn-gold" style={{ padding: "8px 16px", fontSize: "0.82rem", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <Download size={14} /> Export CSV
              </button>
            </div>

            <div style={{ position: "relative", marginBottom: "14px" }}>
              <Search size={14} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.4)" }} />
              <input data-testid="customers-search" type="text" placeholder="Search by name, email or phone..." value={customerSearch} onChange={e => setCustomerSearch(e.target.value)}
                className="cosmic-input" style={{ paddingLeft: "34px" }} />
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem", minWidth: "780px" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    {["Name", "Email", "Phone", "Birth Details", "Plan", "Reports", "Consults", "Spend (₹)", "Signed up", ""].map(h => (
                      <th key={h} style={{ padding: "10px 12px", textAlign: "left", color: "rgba(255,255,255,0.4)", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((c, i) => (
                    <tr key={c.id} data-testid={`customer-row-${i}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                      <td style={{ padding: "10px 12px", color: "white", fontWeight: 600 }}>{c.name || "—"}</td>
                      <td style={{ padding: "10px 12px", color: "#E2D8F0" }}>{c.email}</td>
                      <td style={{ padding: "10px 12px", color: "#A89CB8" }}>{c.phone || "—"}</td>
                      <td data-testid={`customer-birth-${i}`} style={{ padding: "10px 12px", color: "#c4b5fd", fontSize: "0.76rem", minWidth: "170px" }}>{formatBirth(c.birth_details)}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ background: c.plan === "free" ? "rgba(107,114,128,0.18)" : "rgba(255,209,102,0.18)", color: c.plan === "free" ? "#9ca3af" : "#FFD166", padding: "2px 9px", borderRadius: "20px", fontSize: "0.7rem", textTransform: "capitalize", fontWeight: 600 }}>{c.plan?.replace("_", " ")}</span>
                      </td>
                      <td style={{ padding: "10px 12px", color: "white" }}>{c.reports}</td>
                      <td style={{ padding: "10px 12px", color: "white" }}>{c.consultations}</td>
                      <td style={{ padding: "10px 12px", color: "#FFD166", fontWeight: 600 }}>₹{c.lifetime_spend || 0}</td>
                      <td style={{ padding: "10px 12px", color: "#A89CB8", fontSize: "0.76rem" }}>{c.created_at ? new Date(c.created_at).toLocaleDateString("en-IN") : "—"}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <button data-testid={`customer-detail-${i}`} onClick={() => openCustomer(c.id)} className="btn-ghost" style={{ padding: "5px 12px", fontSize: "0.76rem" }}>Details</button>
                      </td>
                    </tr>
                  ))}
                  {filteredCustomers.length === 0 && (
                    <tr><td colSpan={10} style={{ padding: "24px", textAlign: "center", color: "rgba(255,255,255,0.4)" }}>No customers match the search.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Push notifications composer */}
        {tab === "push" && (
          <PushComposer customers={customers} />
        )}

        {/* Kundli-aware WhatsApp outreach: AI draft → preview → link or Twilio send */}
        {tab === "whatsapp" && <AdminWhatsApp />}

        {/* Daily horoscope push — multi-select customers */}
        {tab === "horoscope" && (
          <HoroscopeSender />
        )}

        {/* Email campaign composer */}
        {tab === "email" && (
          <EmailComposer customers={customers} />
        )}

        {/* Weekly digest — AI-generated per-user drafts with admin review */}
        {tab === "weekly digest" && (
          <WeeklyDigest />
        )}

        </AdminOTPGate>
      </div>

      {/* Customer Detail Drawer */}
      {selectedCustomer && (
        <div data-testid="customer-detail-modal" onClick={() => setSelectedCustomer(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)", zIndex: 100, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "60px" }}>
          <div onClick={e => e.stopPropagation()} className="glass-card"
            style={{ maxWidth: "780px", width: "92%", maxHeight: "calc(100vh - 100px)", overflowY: "auto", padding: "24px", border: "1px solid rgba(255,209,102,0.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
              <h3 className="font-cinzel" style={{ color: "#FFD166", fontSize: "1.15rem" }}>Customer Profile</h3>
              <button onClick={() => setSelectedCustomer(null)} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", cursor: "pointer" }}><X size={18} /></button>
            </div>
            {!customerDetail ? (
              <div style={{ padding: "40px", textAlign: "center" }}><div className="cosmic-spinner" style={{ margin: "0 auto" }} /></div>
            ) : customerDetail.error ? (
              <div style={{ color: "#fca5a5" }}>{customerDetail.error}</div>
            ) : (
              <CustomerDetailContent detail={customerDetail} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
